import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Property } from "../models/property.model";
import { cachedMasterDataService } from "../service/cachedMasterData.service";
import { propertyService } from "../service/property.service";

import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import * as Icon from "react-bootstrap-icons";
// RoomTable now shown on its own route (RoomsPage). Inline usage removed.
import { DoorOpen } from "react-bootstrap-icons";
import Swal from "sweetalert2";
import PageHeader from "./PageHeader";

/* ─────────────────────────── Helpers ─────────────────────────── */
type IdApiResponse = number[] | { $values: number[] } | undefined | null;
const extractIds = (data: IdApiResponse): number[] =>
  Array.isArray(data)
    ? data.map(Number)
    : data &&
      typeof data === "object" &&
      "$values" in data &&
      Array.isArray((data as any).$values)
    ? (data as any).$values.map(Number)
    : [];

/* ────────────────────────── Component ────────────────────────── */
const Properties: React.FC = () => {
  // Handler for Assign All button

  /** navigation */
  const navigate = useNavigate();

  const { state } = useLocation();
  const { rmcId: rmcIdParam, clientId: clientIdParam } = useParams<{
    rmcId: string;
    clientId: string;
  }>();
  const clientId = clientIdParam ? Number(clientIdParam) : state?.clientId;
  const rmcId = rmcIdParam ? Number(rmcIdParam) : state?.rmcId;
  const [clientName, setClientName] = useState<string | undefined>(
    state?.clientName
  );
  const rmcClientMappingId = state?.rmcClientMappingId;
  const proposalId = state.proposalId;

  /** auth */
  const userRole = useSelector((s: any) => s.auth.userRole) ?? "";
  const consultantRmcId = useSelector((s: any) => s.auth.rmcId) ?? "";

  /** local state */
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);
  const [citiesLoaded, setCitiesLoaded] = useState(false);
  const [selectedCityId, setSelectedCityId] = useState<number | "">("");
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedRows, setSelectedRows] = useState<Property[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null
  );
  // Room-related inline state removed (handled by RoomsPage route)
  const handleAssignAll = async () => {
    if (!rmcClientMappingId || !selectedCityId) return;
    try {
      // Call the mappingAllProperty service with rmcClientMappingId and selectedCityId
      await propertyService.mappingAllProperty(
        rmcClientMappingId,
        selectedCityId
      );
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: `Assigned all properties.`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
      // Reload properties and selection
      const cityId = Number(selectedCityId);
      const props =
        userRole === "RMCAdmin" || userRole === "RMCUser"
          ? await propertyService.getPropertiesByCityIdAndRmcId(
              cityId,
              Number(rmcClientMappingId) || 0
            )
          : await propertyService.getPropertiesByCityId(cityId);
      setProperties(props);
      if (rmcClientMappingId) {
        const rawIds = await propertyService.getPropertyIdsbyRMCClientMappingId(
          rmcClientMappingId,
          cityId,
          userRole === "RMCAdmin" || userRole === "RMCUser"
        );
        const ids = extractIds(rawIds);
        setSelectedRows(props.filter((p) => ids.includes(Number(p.id))));
      } else {
        setSelectedRows([]);
      }
    } catch (e) {
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Assign all failed.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    }
  };
  /* ─────────── load cities once ─────────── */
  useEffect(() => {
    console.log(proposalId);
    if (citiesLoaded) return; // Prevent duplicate API calls

    (async () => {
      try {
        const cityList = await cachedMasterDataService.getCitiesList();
        setCities(
          (cityList ?? []).map((c: any) => ({
            id: Number(c.id),
            name: c.name,
          }))
        );
        setCitiesLoaded(true);
      } catch (e) {
        console.error("Error fetching cities", e);
      }
    })();
  }, [citiesLoaded]);

  /* ─────────── load properties every time city/role changes ─────────── */
  useEffect(() => {
    const cityId = Number(selectedCityId) || undefined;
    if (!cityId) {
      setProperties([]);
      setSelectedRows([]);
      return;
    }
    (async () => {
      try {
        /* main list */
        const props =
          userRole === "RMCAdmin" || userRole === "RMCUser"
            ? await propertyService.getPropertiesByCityIdAndRmcId(
                cityId,
                Number(rmcClientMappingId) || 0
              )
            : await propertyService.getPropertiesByCityIdAndProposalId(
                cityId,
                proposalId
              );

        setProperties(props);

        /* pre-select based on mapping */
        if (rmcClientMappingId) {
          const rawIds =
            await propertyService.getPropertyIdsbyRMCClientMappingId(
              rmcClientMappingId,
              cityId,
              userRole === "RMCAdmin" || userRole === "RMCUser"
            );
          console.log("Fetched rmcClientMappingId:", rmcClientMappingId);
          const ids = extractIds(rawIds);
          setSelectedRows(props.filter((p) => ids.includes(Number(p.id))));
        } else {
          setSelectedRows([]);
        }
      } catch (e) {
        console.error("Error fetching properties", e);
      }
    })();
  }, [selectedCityId, userRole, consultantRmcId, rmcClientMappingId]);

  // Add a function to reload properties and selection
  const reloadProperties = useCallback(async () => {
    const cityId = Number(selectedCityId);
    if (!cityId) {
      setProperties([]);
      setSelectedRows([]);
      return;
    }
    try {
      const props =
        userRole === "RMCAdmin" || userRole === "RMCUser"
          ? await propertyService.getPropertiesByCityIdAndRmcId(
              cityId,
              Number(rmcClientMappingId) || 0
            )
          : await propertyService.getPropertiesByCityIdAndProposalId(
              cityId,
              proposalId
            );
      setProperties(props);
      if (rmcClientMappingId) {
        const rawIds = await propertyService.getPropertyIdsbyRMCClientMappingId(
          rmcClientMappingId,
          cityId,
          userRole === "RMCAdmin" || userRole === "RMCUser"
        );
        const ids = extractIds(rawIds);
        setSelectedRows(props.filter((p) => ids.includes(Number(p.id))));
      } else {
        setSelectedRows([]);
      }
    } catch (e) {
      console.error("Error fetching properties", e);
    }
  }, [selectedCityId, userRole, rmcClientMappingId, proposalId]);

  /* ─────────── helper: save mappings ─────────── */
  const saveMappings = async () => {
    // if (!clientId || selectedRows.length === 0) return;

    const payload = selectedRows.map((r) => ({
      proposalId: Number(proposalId),
      propertyId: Number(r.id),
      isRMCCLient: true,
    }));

    try {
      if (userRole === "RMCAdmin" || userRole === "RMCUser") {
        await propertyService.createRMCClientPropertyMappings(
          rmcClientMappingId,
          Number(selectedCityId),
          payload
        );
      } else {
        await propertyService.createClientPropertyMappings(
          rmcClientMappingId,
          Number(selectedCityId),
          payload
        );
      }
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Property mapped successfully.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
      await reloadProperties(); // Refresh data after saving
    } catch (e) {
      console.error("Error saving mappings", e);
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Property mapping failed.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    }
  };
  // const handleRowClick = async (event: DataTableRowClickEvent) => {
  //     const property = event.data as Property;
  //     const propertyId = property.id;
  //     if (propertyId === undefined) {
  //       console.error('Property ID is undefined');
  //       return;
  //     }
  //     try {
  //       const details = await roomService.getByPropertyId(propertyId);
  //       setSelectedProperty(property);
  //       setRoomDetails(details);
  //     } catch (error) {
  //       setSelectedProperty(property);
  //       setRoomDetails([]);
  //       console.error('Error fetching property details:', error);
  //     }
  //   };

  /* ─────────── derived: filtered list ─────────── */
  const filtered = properties.filter((p) =>
    [p.name, p.description, p.address, p.termsAndConditions, p.website].some(
      (f) => (f ?? "").toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  /* ─────────── Navigate to rooms nested route ─────────── */
  const handleOpenRoomTable = (property: Property) => {
    if (rmcId && clientId) {
      navigate(
        `/rmc/${rmcId}/clients/${clientId}/properties/${property.id}/rooms`,
        {
          state: {
            rmcId,
            clientId,
            clientName,
            propertyName: property.name,
            proposalId,
            clientPropertyMappingId: property.proposalPropertyMappings?.map(
              (m) => m.id
            ),
          },
        }
      );
    } else {
      // fallback legacy route pattern if needed
      navigate(`/properties/${property.id}/rooms`, {
        state: { propertyName: property.name, proposalId },
      });
    }
  };
  const handleEdit = useCallback(
    (rowData: Property) => {
      navigate(`/manage-property/edit/${rowData.id}`, {
        state: {
          mode: "edit",
          propertyId: rowData.id,
          rmcClientMappingId: rmcClientMappingId,
        },
      });
    },
    [navigate]
  );

  useEffect(() => {
    if (!clientName && clientId && rmcId) {
      cachedMasterDataService
        .getClientsByRmcId?.(rmcId)
        .then((list: any[]) => {
          const found = (list || []).find(
            (c) => Number(c.id) === Number(clientId)
          );
          if (found?.name) setClientName(found.name);
        })
        .catch(() => {});
    }
  }, [clientName, clientId, rmcId]);

  /* ─────────── render ─────────── */
  return (
    <div className="content">
      <PageHeader
        showBackButton={true}
        backTo={
          rmcId && clientId
            ? `/rmc/${rmcId}/clients/${clientId}/proposal`
            : "/clients"
        }
        backState={
          rmcId && clientId
            ? { rmcId, clientId, clientName, rmcClientMappingId }
            : undefined
        }
        title={
          clientName ? `Properties List - ${clientName}` : "Properties List"
        }
      />
      {/* body */}
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-3">
              <>
                {/* filters */}
                <div className="d-flex align-items-center gap-2 my-3">
                  <Dropdown
                    value={selectedCityId}
                    options={cities.map((c) => ({
                      label: c.name,
                      value: c.id,
                    }))}
                    onChange={(e) => setSelectedCityId(e.value)}
                    placeholder="Select City"
                    className="w-25"
                    style={{ minWidth: 250, height: 48, fontSize: 18 }}
                    appendTo="self"
                  />
                  <button
                    className="btn btn-outline-secondary btn-sm ms-2"
                    type="button"
                    onClick={handleAssignAll}
                    disabled={filtered.length === 0}
                    style={{ minWidth: 150, height: 48, fontSize: 18 }}
                  >
                    Assign All
                  </button>
                  <div className="ms-auto" style={{ minWidth: 300 }}>
                    <IconField iconPosition="left" style={{ width: "100%" }}>
                      <InputIcon>
                        <Icon.Search size={16} />
                      </InputIcon>
                      <InputText
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search"
                        style={{ minWidth: 300, height: 48, fontSize: 18 }}
                      />
                    </IconField>
                  </div>
                </div>

                {/* table */}
                <DataTable
                  value={filtered}
                  dataKey="id"
                  selection={selectedRows}
                  onSelectionChange={(e) => setSelectedRows(e.value)}
                  selectionMode="checkbox"
                  paginator
                  rows={10}
                  rowsPerPageOptions={[5, 10, 20]}
                  stripedRows
                  sortMode="multiple"
                  emptyMessage={
                    selectedCityId === ""
                      ? "Select the city."
                      : "No properties found."
                  }
                  className="grid-table"
                >
                  <Column
                    selectionMode="multiple"
                    headerStyle={{ width: "3rem" }}
                  />
                  <Column field="id" header="Id" sortable />
                  <Column field="name" header="Name" sortable />
                  <Column
                    field="description"
                    header="Description"
                    sortable
                    body={(row) => (
                      <span
                        title={row.description}
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "pre-line",
                          wordBreak: "break-word",
                          maxWidth: 400,
                          height: "4.5em",
                        }}
                      >
                        {row.description}
                      </span>
                    )}
                  />
                  {/* <Column field="termsAndConditions" header="Terms & Conditions" sortable /> */}
                  <Column field="address" header="Address" sortable />
                  <Column field="website" header="Website" sortable />
                  {/* Actions column */}
                  <Column
                    header="Actions"
                    body={(row) => (
                      <div className="d-flex justify-content-center gap-2">
                        {/* Manage Rooms Button */}
                        <button
                          className="btn btn-link p-0"
                          title="Manage Rooms"
                          onClick={() => handleOpenRoomTable(row)}
                        >
                          <DoorOpen size={20} />
                        </button>

                        {/* Edit Button */}
                        <button
                          className="btn btn-link p-0"
                          title={
                            userRole === "Administrator"
                              ? "Edit Property"
                              : "View Property"
                          }
                          onClick={() => handleEdit(row)}
                        >
                          <i
                            className={`bi ${
                              userRole === "Administrator"
                                ? "bi-pencil"
                                : "bi-eye"
                            }`}
                            style={{ fontSize: "18px" }}
                          ></i>
                        </button>
                      </div>
                    )}
                    style={{ width: "6rem", textAlign: "center" }}
                  />
                </DataTable>

                {/* actions */}
                <div className="d-flex justify-content-end mt-3">
                  <button
                    className="btn btn-success me-2"
                    onClick={saveMappings}
                  >
                    Save Changes
                  </button>
                </div>
              </>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Properties;
